import graphql from "babel-plugin-relay/macro";
import { useEffect, type PropsWithChildren } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
  type PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";

import { QueueItGate } from "./QueueItGate";
import type {
  ClubQueueItPreflightQuery,
  ClubQueueItPreflightQuery$data,
} from "./__generated__/ClubQueueItPreflightQuery.graphql";

type Props = PropsWithChildren<{
  clubSlug: string;
}>;

/**
 * Reads only the public Queue-it configuration before the app creates the
 * private Relay environment or loads the full club navigator query.
 */
export const ClubQueueItPreflight = ({ children, clubSlug }: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<ClubQueueItPreflightQuery>(
    ClubQueueItPreflightQueryString
  );

  useEffect(() => {
    loadQuery({ clubSlug }, { fetchPolicy: "network-only" });
  }, [clubSlug, loadQuery]);

  if (!queryRef) {
    return <Loader />;
  }

  return (
    <ErrorBoundary
      fallbackRender={() => <>{children}</>}
      resetKeys={[clubSlug]}
    >
      <ClubQueueItPreflightResult clubSlug={clubSlug} queryRef={queryRef}>
        {children}
      </ClubQueueItPreflightResult>
    </ErrorBoundary>
  );
};

const ClubQueueItPreflightResult = ({
  children,
  clubSlug,
  queryRef,
}: PropsWithChildren<{
  clubSlug: string;
  queryRef: PreloadedQuery<ClubQueueItPreflightQuery>;
}>) => {
  const data = usePreloadedQuery<ClubQueueItPreflightQuery>(
    ClubQueueItPreflightQueryString,
    queryRef
  );

  return (
    <QueueItGate
      clubSlug={clubSlug}
      configuration={getQueueItConfiguration(data)}
    >
      {children}
    </QueueItGate>
  );
};

const getQueueItConfiguration = (data: ClubQueueItPreflightQuery$data) => {
  const queueIt = data.unauthenticatedClub?.queueIt;

  if (!queueIt?.customerId) {
    return null;
  }

  return {
    customerId: queueIt.customerId,
    nativeWaitingRoomAlias: queueIt.nativeWaitingRoomAlias ?? null,
  };
};

export const ClubQueueItPreflightQueryString = graphql`
  query ClubQueueItPreflightQuery($clubSlug: String!) {
    unauthenticatedClub(slug: $clubSlug) {
      queueIt {
        customerId
        nativeWaitingRoomAlias
      }
    }
  }
`;
